import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  createTheme,
  CssBaseline,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DataGridPro, GridColDef, GridOverlay } from "@mui/x-data-grid-pro";
import { Add, Dashboard } from "@mui/icons-material";
// import Pagination from "../../../Components/Common/TablePro/Pagination";
import { useCustomLocaleText } from "../../../Components/Common/useCustomLocaleText";
import useDynamicHeight from "../../../Components/scrollable/useDynamicHeight";
import { useAppSelector } from "@/app/hooks";
import { useParams } from "react-router-dom";
import { useGetSprintListQuery } from "@/features/bughurd/api";
import Pagination from "@/Components/Common/filters/Pagination";
import PhantomLoader from "@/Components/Common/phantomLoader";
import SprintSelectionDialog from "./Sprint/SprintSelectionDialog";
import TemplateSelectionPage from "./TemplateSelectionPage";
import RetrospectiveSessionPage from "./RetrospectiveSessionPage";
import Retrospectives from "@/pages/Retrospectives";
import FourLRetrospective from "@/pages/Retrospectives/components/FourLRetrospective";
import FlowDiagram from "@/pages/Retrospectives/components/FlowDiagram";
import RetrospectiveHeader from "@/pages/Retrospectives/components/RetrospectiveHeader";
import StartStopContinueRetrospective from "@/pages/Retrospectives/components/StartStopContinueRetrospective";
import GenericRetrospective from "@/pages/Retrospectives/components/GenericRetrospective";
import SailboatRetrospective from "@/pages/Retrospectives/components/SailboatRetrospective";

// Helper function to get completed sprints
const getCompletedSprints = (sprints: any[]) => {
  return sprints
    .filter(
      (sprint) => sprint.status === "ARCHIVE" || sprint.status === "COMPLETED"
    )
    .map((sprint, index) => ({
      id: sprint._id,
      sprintNumber: sprint.name || `Sprint ${index + 1}`,
      startDate: sprint.startDate || "",
      endDate: sprint.endDate || "",
      retrospectiveDate: "",
      status: sprint.status,
      retro: sprint.retro || null,
      isRetro: sprint.isRetro || false,
    }));
};

// Helper function to normalize template ID
const normalizeTemplateId = (templateId: string) => {
  // Remove version suffix if present (e.g., "mad-sad-glad-v1" -> "mad-sad-glad")
  return templateId.replace(/-v\d+$/, "");
};

interface RetrospectivesTabProps {
  reactFlow: any;
  parentData?: any;
}

const RetrospectivesTab: React.FC<RetrospectivesTabProps> = ({
  reactFlow,
  parentData,
}) => {
  const { id } = useParams<{ id: string }>();
  const theme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#1976d2",
      },
      background: {
        default: "#fafafa",
      },
    },
  });
  const { dateFormat, recordsPerPage } = useAppSelector(
    (state) => state.appGlobal
  );
  const { t } = useTranslation(["bugherd"]);
  const localeText = useCustomLocaleText();
  const dynamicHeight = `calc(${useDynamicHeight()} - 90px)`;

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: 10,
    page: 1,
  });

  // State for retrospective flow
  const [showSprintDialog, setShowSprintDialog] = useState(false);
  const [showTemplateSelection, setShowTemplateSelection] = useState(false);
  const [showRetrospectiveSession, setShowRetrospectiveSession] =
    useState(false);
  const [selectedSprintId, setSelectedSprintId] = useState("");
  const [selectedSprintName, setSelectedSprintName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [showRealtimeBoard, setShowRealtimeBoard] = useState(false);
  const [cameFromTemplateSelection, setCameFromTemplateSelection] =
    useState(false);

  // API call to get sprints
  const {
    data: sprintListData,
    isLoading: isSprintLoading,
    isFetching: isSprintFetching,
    refetch: refetchSprints,
  } = useGetSprintListQuery(
    {
      projectId: id || "",
      page: paginationModel?.page || 1,
      limit: paginationModel?.pageSize || 10,
      statuses: ["ARCHIVE", "COMPLETED"],
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    }
  );

  // Get completed sprints from API data
  const sprints = getCompletedSprints(
    sprintListData?.data?.decryptedData?.items || []
  );

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, pageSize: recordsPerPage }));
  }, [recordsPerPage]);

  const handleStartRetrospective = useCallback(
    (sprintId: string) => {
      const sprint = sprints.find((s) => s.id === sprintId);
      if (sprint) {
        setSelectedSprintId(sprintId);
        setSelectedSprintName(sprint.sprintNumber);

        // Check if retrospective already exists
        if (sprint.isRetro && sprint.retro && sprint.retro.length > 0) {
          // Use existing retrospective template
          const existingRetro = sprint.retro[0];
          const normalizedTemplateId = normalizeTemplateId(
            existingRetro.templateId
          );
          setSelectedTemplateId(normalizedTemplateId);
          setCameFromTemplateSelection(false);
          setShowRetrospectiveSession(true);
        } else {
          // Show template selection for new retrospective
          setShowTemplateSelection(true);
        }
      }
    },
    [sprints]
  );

  const handleHeaderStartRetrospective = useCallback(() => {
    setShowSprintDialog(true);
  }, []);

  const handleSprintDialogContinue = useCallback(
    (sprintId: string) => {
      const sprint = sprints.find((s) => s.id === sprintId);
      if (sprint) {
        setSelectedSprintId(sprintId);
        setSelectedSprintName(sprint.sprintNumber);
        setShowSprintDialog(false);

        // Check if retrospective already exists
        if (sprint.isRetro && sprint.retro && sprint.retro.length > 0) {
          // Use existing retrospective template
          const existingRetro = sprint.retro[0];
          const normalizedTemplateId = normalizeTemplateId(
            existingRetro.templateId
          );
          setSelectedTemplateId(normalizedTemplateId);
          setCameFromTemplateSelection(false);
          setShowRetrospectiveSession(true);
        } else {
          // Show template selection for new retrospective
          setShowTemplateSelection(true);
        }
      }
    },
    [sprints]
  );

  const handleTemplateSelect = useCallback(
    (templateId: string, sprintId: string) => {
      setSelectedTemplateId(templateId);
      setShowTemplateSelection(false);
      setCameFromTemplateSelection(true);
      setShowRetrospectiveSession(true);
    },
    []
  );

  const handleBackToTable = useCallback(() => {
    setShowTemplateSelection(false);
    setShowRetrospectiveSession(false);
    setSelectedSprintId("");
    setSelectedSprintName("");
    setSelectedTemplateId("");
    setCameFromTemplateSelection(false);

    // Refetch sprints to get updated data
    refetchSprints();
  }, [refetchSprints]);

  const handleBackToTemplateSelection = useCallback(() => {
    setShowRetrospectiveSession(false);
    setShowTemplateSelection(true);
    refetchSprints();
  }, [refetchSprints]);

  // Create conditional back handler
  const getBackHandler = useCallback(() => {
    return cameFromTemplateSelection
      ? handleBackToTemplateSelection
      : handleBackToTable;
  }, [
    cameFromTemplateSelection,
    handleBackToTemplateSelection,
    handleBackToTable,
  ]);

  const getActionButtonText = useCallback(
    (sprint: any) => {
      if (sprint.isRetro && sprint.retro && sprint.retro.length > 0) {
        return t("Continue");
      }
      return t("Start");
    },
    [t]
  );

  const columns: GridColDef[] = [
    {
      field: "sprintNumber",
      headerName: t("Sprint #"),
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight={500}
          display="flex"
          alignItems="center"
          height="100%"
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "startDate",
      headerName: t("Start Date"),
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          display="flex"
          alignItems="center"
          height="100%"
        >
          {moment(params.value).format(dateFormat || "MMM DD, YYYY")}
        </Typography>
      ),
    },
    {
      field: "endDate",
      headerName: t("End Date"),
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          display="flex"
          alignItems="center"
          height="100%"
        >
          {moment(params.value).format(dateFormat || "MMM DD, YYYY")}
        </Typography>
      ),
    },
    {
      field: "retrospectiveDate",
      headerName: t("Retrospective Date"),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const sprint = sprints.find((s) => s.id === params.row.id);
        let retroDate = "N/A";

        if (sprint?.isRetro && sprint?.retro && sprint.retro.length > 0) {
          const firstRetro = sprint.retro[0];
          if (firstRetro.startDate) {
            retroDate = moment(firstRetro.startDate).format(
              dateFormat || "MMM DD, YYYY"
            );
          }
        }

        return (
          <Typography
            variant="body2"
            display="flex"
            alignItems="center"
            height="100%"
          >
            {retroDate}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: t("Action"),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleStartRetrospective(params.row.id)}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            px: 2,
          }}
        >
          {getActionButtonText(params.row)}
        </Button>
      ),
    },
  ];

  if (showRealtimeBoard) {
    return (
      <Retrospectives
        onBack={() => {
          setShowRealtimeBoard(false);
          handleBackToTable();
        }}
        sprintId={"689d8fdc9071b950bf248f01"}
        sprintName={"ST Sprint 32"}
        reactFlow={reactFlow}
        parentData={parentData}
      />
    );
  }

  const renderTemplate = () => {
    const backHandler = getBackHandler();

    switch (selectedTemplateId) {
      case "4l-retrospective":
        return (
          <FourLRetrospective
            onBack={backHandler}
            templateId={selectedTemplateId}
            sprintId={selectedSprintId}
            sprintName={selectedSprintName}
            reactFlow={reactFlow}
          />
        );
      case "board_1":
        return (
          <FlowDiagram
            onBack={backHandler}
            templateId={selectedTemplateId}
            sprintId={selectedSprintId}
            sprintName={selectedSprintName}
            reactFlow={reactFlow}
          />
        );
      case "start-stop-continue":
        return (
          <GenericRetrospective
            onBack={backHandler}
            sprintId={selectedSprintId}
            sprintName={selectedSprintName}
            reactFlow={reactFlow}
            templateId="start-stop-continue"
          />
        );
      case "mad-sad-glad":
        return (
          <GenericRetrospective
            onBack={backHandler}
            sprintId={selectedSprintId}
            sprintName={selectedSprintName}
            reactFlow={reactFlow}
            templateId="mad-sad-glad"
          />
        );
      case "sailboat":
        return (
          <SailboatRetrospective
            onBack={backHandler}
            sprintId={selectedSprintId}
            sprintName={selectedSprintName}
            reactFlow={reactFlow}
            templateId={selectedTemplateId}
          />
        );
      default:
        return null;
    }
  };

  // Show retrospective session page if active
  if (showRetrospectiveSession) {
    return (
      <Box width={"100%"} height="100%">
        {renderTemplate()}
      </Box>
    );
  }

  // Show template selection page if active
  if (showTemplateSelection) {
    return (
      <TemplateSelectionPage
        onBack={handleBackToTable}
        onTemplateSelect={handleTemplateSelect}
        selectedSprintId={selectedSprintId}
        selectedSprintName={selectedSprintName}
      />
    );
  }

  return (
    <Container maxWidth="xl" sx={{ height: "100%", py: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" fontWeight={600}>
          {t("Retrospectives")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleHeaderStartRetrospective}
            sx={{
              textTransform: "none",
            }}
          >
            {t("Start Retrospective")}
          </Button>
        </Box>
      </Box>

      <DataGridPro
        getRowId={(row) => row.id}
        columns={columns}
        rows={sprints}
        disableColumnMenu
        pagination
        loading={isSprintLoading || isSprintFetching}
        rowSelection={false}
        disableMultipleColumnsSorting
        localeText={localeText}
        slots={{
          pagination: () => (
            <Pagination
              total={
                sprintListData?.data?.decryptedData?.totalCount ||
                sprints.length
              }
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
            />
          ),
          noRowsOverlay: () => (
            <GridOverlay>
              <div>{t("No Data Found")}</div>
            </GridOverlay>
          ),
          loadingOverlay: () => (
            <GridOverlay>
              <PhantomLoader />
            </GridOverlay>
          ),
        }}
        style={{
          height: dynamicHeight,
        }}
      />

      {/* Sprint Selection Dialog */}
      <SprintSelectionDialog
        open={showSprintDialog}
        onClose={() => setShowSprintDialog(false)}
        onContinue={handleSprintDialogContinue}
        sprints={sprints}
        loading={isSprintLoading}
      />
    </Container>
  );
};

export default RetrospectivesTab;
